import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, IconButton, MenuItem, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback } from 'react';
import { NumericInput } from '../../Input/NumericInput';
import { Select } from '../../Select/Select';
import { CompoundOptions } from '../CompoundPage.hooks';
import { INITIAL_VALUES, investFrequencyMap, schema } from './CompoundForm.hooks';
import styles from './CompoundForm.module.css';

export type CompoundFormProps = {
  onSubmit: (options: CompoundOptions) => void;
  className?: string;
};

const FieldTooltip = ({ text }: { text: string }) => (
  <Tooltip title={text} arrow placement="top">
    <IconButton size="small" sx={{ ml: 0.5, p: 0.25, color: '#9ca3af' }}>
      <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
    </IconButton>
  </Tooltip>
);

export const CompoundForm = ({ onSubmit, className }: CompoundFormProps) => {
  const handleSubmit = useCallback((options: CompoundOptions) => onSubmit(options), [onSubmit]);

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={schema} onSubmit={handleSubmit}>
      {({ errors, setFieldValue }) => (
        <Form className={classNames(styles.root, className)}>
          <div className={styles.body}>
            <Field name="initialAsset">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      초기 투자금 (원)
                      <FieldTooltip text="현재 투자에 바로 쓸 수 있는 목돈입니다. 0원도 가능합니다." />
                    </span>
                  }
                  placeholder="10,000,000"
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => setFieldValue(field.name, values.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.initialAsset && <div className={styles.error}>{errors.initialAsset}</div>}

            <Field name="monthlyContribution">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      정기 납입금 (원)
                      <FieldTooltip text="복리 효과의 90%는 꾸준한 납입에서 나옵니다." />
                    </span>
                  }
                  placeholder="500,000"
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => setFieldValue(field.name, values.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.monthlyContribution && (
              <div className={styles.error}>{errors.monthlyContribution}</div>
            )}

            <Field name="investFrequency">
              {({ field }: FieldProps) => (
                <Select
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      투자 주기
                      <FieldTooltip text="납입 주기. 매월 납입이 복리 효과가 가장 큽니다." />
                    </span>
                  }
                  {...field}
                >
                  <MenuItem value={12}>{investFrequencyMap[12]}</MenuItem>
                  <MenuItem value={4}>{investFrequencyMap[4]}</MenuItem>
                  <MenuItem value={1}>{investFrequencyMap[1]}</MenuItem>
                </Select>
              )}
            </Field>
            {errors.investFrequency && <div className={styles.error}>{errors.investFrequency}</div>}

            <Field name="period">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      투자 기간 (년)
                      <FieldTooltip text="은퇴 예정 나이에서 현재 나이를 빼면 됩니다." />
                    </span>
                  }
                  placeholder="20"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => setFieldValue(field.name, values.floatValue)}
                />
              )}
            </Field>
            {errors.period && <div className={styles.error}>{errors.period}</div>}

            <Field name="annualReturnRate">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      연 수익률 (%)
                      <FieldTooltip text="보수: 3~4%(예금/채권), 중간: 6~8%(분산투자), 낙관: 9%+(주식)" />
                    </span>
                  }
                  placeholder="7"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => setFieldValue(field.name, values.floatValue)}
                />
              )}
            </Field>
            {errors.annualReturnRate && (
              <div className={styles.error}>{errors.annualReturnRate}</div>
            )}
          </div>
          <div className={styles.action}>
            <Button className={styles.button} variant="contained" type="submit">
              계산하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
